import React, { useMemo } from 'react';
import { Box, Stack } from '@mui/material';
import { VegaLite } from 'react-vega';
import { ChartData } from 'app/concierge/types';
import { VisualizationSpec } from 'vega-embed';
import { useResizeDetector } from 'react-resize-detector';
import { UserAuth } from 'store/auth';
import { useAppSelector } from 'store/hooks';
import { processChartConfig } from './config-chart-processor';
import ExpandButton from './expand-button';

interface IProps {
    chartData: ChartData;
    conciergeResponse: React.ReactNode;
}

function isLineChart(spec: any): boolean {
    const mark = spec.mark;
    const markType = typeof mark === 'string' ? mark : mark?.type;
    return markType === 'line' || markType === 'area' || markType === 'trail';
}

export default function JsonChart({ chartData, conciergeResponse }: IProps) {
    const { width, ref } = useResizeDetector();
    const isLine = isLineChart(chartData.data);

    const aspectRatio = 1;

    const userAuth: UserAuth = useAppSelector((state) => state.auth);
    const brandThemeProperties = userAuth?.userInfo?.brandThemeProperties ?? {};

    const { barColors, lineColors } = useMemo(() => {
        const sorted = [...brandThemeProperties.charting_colors].sort((a, b) => a.display_order - b.display_order);

        return {
            barColors: sorted.map((c) => c.bar_color),
            lineColors: sorted.map((c) => c.line_color),
        };
    }, [brandThemeProperties.charting_colors]);

    const adjustedHeight = useMemo(() => (width ? Math.round(width / aspectRatio) : undefined), [width, aspectRatio]);
    const colors = isLine ? lineColors : barColors;

    const adjustedSpec: VisualizationSpec = useMemo(() => {
        return processChartConfig({
            spec: chartData.data,
            width,
            height: adjustedHeight,
            colors,
            isBig: false,
        });
    }, [chartData.data, width, adjustedHeight, colors]);

    const adjustedSpecPopup: VisualizationSpec = useMemo(() => {
        return processChartConfig({
            spec: chartData.data,
            width: 800,
            height: 600,
            colors,
            isBig: true,
        });
    }, [chartData.data, width, adjustedHeight, colors]);

    return (
        <>
            <Stack sx={{ justifyContent: 'center', alignItems: 'center' }}>
                <Box
                    ref={ref}
                    sx={{
                        width: '100%',
                        height: adjustedHeight,
                        paddingRight: '40px',
                        paddingTop: '5px',
                        position: 'relative',
                    }}
                >
                    {width && adjustedHeight && (
                        <VegaLite
                            spec={adjustedSpec}
                            onError={(error) => console.error('Chart rendering error:', error)}
                        />
                    )}
                </Box>
                <ExpandButton
                    conciergeMessage={conciergeResponse}
                    content={
                        <Box>
                            <VegaLite
                                spec={adjustedSpecPopup}
                                onError={(error) => console.error('Chart rendering error:', error)}
                            />
                        </Box>
                    }
                />
            </Stack>
        </>
    );
}
